import { type RefObject, useEffect, useRef } from 'react'

import { trackEvent } from '~/shared/lib/analytics'

import { DOCUMENT_STEP_FIRST_INTERACTION } from '../config'
import { useStepSession } from './step-session-context'

function isInteractiveElement(target: HTMLElement): boolean {
  return (
    target.closest('input, textarea, select, button') != null ||
    target.closest('[role="combobox"], [role="listbox"], [role="checkbox"]') != null ||
    target.closest('[data-track-name]') != null
  )
}

function getElementName(target: HTMLElement): string | undefined {
  if (target.dataset.trackName) return target.dataset.trackName
  if (target.dataset.fieldName) return target.dataset.fieldName
  if ((target as HTMLInputElement).name) return (target as HTMLInputElement).name
  const ancestor =
    target.closest<HTMLElement>('[data-track-name]') ??
    target.closest<HTMLElement>('[data-field-name]') ??
    target.closest<HTMLElement>('[name]')
  if (ancestor) {
    return (
      ancestor.dataset.trackName ??
      ancestor.dataset.fieldName ??
      (ancestor as HTMLInputElement).name
    )
  }
  return undefined
}

export function useFirstInteraction(containerRef: RefObject<HTMLElement | null>) {
  const { step, documentKey } = useStepSession()
  const hasFiredRef = useRef(false)

  useEffect(() => {
    hasFiredRef.current = false
  }, [step, documentKey])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const handler = (e: PointerEvent | KeyboardEvent) => {
      if (hasFiredRef.current) return
      const target = e.target as HTMLElement
      if (!isInteractiveElement(target)) return

      hasFiredRef.current = true
      trackEvent(DOCUMENT_STEP_FIRST_INTERACTION, {
        step,
        document_key: documentKey,
        element_name: getElementName(target),
      })
      el.removeEventListener('pointerdown', handler)
      el.removeEventListener('keydown', handler)
    }

    el.addEventListener('pointerdown', handler, { passive: true })
    el.addEventListener('keydown', handler, { passive: true })
    return () => {
      el.removeEventListener('pointerdown', handler)
      el.removeEventListener('keydown', handler)
    }
  }, [containerRef, step, documentKey])
}
